import { Alert } from "@ui/Alert";
import { Button, type ButtonTheme } from "@ui/Button";
import { Card } from "@ui/Card";
import { CardList } from "@ui/CardList";
import { formatBytesWithMagnitude } from "@ui/format";
import { NoMatchingResults } from "@ui/molecules/NoMatchingResults/NoMatchingResults";
import { Search } from "@ui/Search";
import { Shimmer } from "@ui/Shimmer";
import { Stack } from "@ui/Stack";
import { Text } from "@ui/typography/Text";
import { dayBucketLabel, timeAgo } from "@utils/time";
import type { useSearch } from "@utils/usePagination";
import {
	createEffect,
	createMemo,
	For,
	Match,
	type Resource,
	Switch,
} from "solid-js";
import { createStore, reconcile } from "solid-js/store";
import { TransitionGroup } from "solid-transition-group";
import type { ArtifactListResponse, ArtifactListResponseItem } from "@/client";
import { ArtifactItemThumbnail } from "../../../artifacts/list/components/ArtifactItemThumbnail";
import "./AnswerPanel.css";

declare const pageVars: { questionId: string };

const mockAnswer: ArtifactListResponseItem = {
	public_id: "doc_ABCxyz123",
	title: "Report 1",
	summary: "Report 1 summary",
	documents: [{ file: { content_type: "application/pdf", bytes: 1000 } }],
};

type Props = {
	answers: Resource<ArtifactListResponse>;
	toggleFavourite: (public_id: string) => Promise<void>;
	/** Whether an item's title/summary are still being generated server-side after being saved. */
	isMetadataPending: (public_id: string | undefined) => boolean;
	searchProps: ReturnType<typeof useSearch>;
	/** Called once the chat has scrolled to the chosen answer. */
	onAnswerSelected?: () => void;
};
export const AnswerPanel = (props: Props) => {
	// Keyed store instead of reading `props.answers()` directly: reconciling by
	// `public_id` keeps the same object reference for rows that haven't
	// changed across a refetch (search, favouriting, saving/unsaving an
	// artifact), so <For> only mounts/unmounts the rows that actually differ.
	// TransitionGroup below turns those deltas into enter/exit animations
	// instead of the whole list repainting at once.
	const [items, setItems] = createStore<ArtifactListResponseItem[]>([]);
	createEffect(() => {
		const latest = props.answers.latest;
		if (latest) {
			setItems(reconcile(latest.items ?? [], { key: "public_id" }));
		}
	});

	// Grouped by calendar day (not `timeAgo`'s second-granular string) so a
	// group stays put as items come and go, letting the TransitionGroup below
	// see real add/remove diffs within it instead of every item mounting its
	// own transient, single-item group.
	const groupedAnswers = createMemo(() => {
		return items.reduce(
			(prev, next) => {
				const dayLabel = dayBucketLabel(next.created_at);
				if (!prev[dayLabel]) {
					prev[dayLabel] = [];
				}
				prev[dayLabel].push(next);
				return prev;
			},
			{} as Record<string, ArtifactListResponseItem[]>,
		);
	});

	// Only show the skeleton for the very first load. Once there's a previous
	// result to keep displaying, a refetch (e.g. from search or the
	// asset-state event) shouldn't blank the panel out from under the user.
	const showInitialLoading = () =>
		props.answers.loading && !props.answers.latest;

	return (
		<Stack class="answer-panel-content">
			<Search
				value={props.searchProps.searchInput()}
				onSearch={props.searchProps.handleSearch}
				placeholder="Search artifacts"
				onClear={props.searchProps.handleClear}
			/>
			<Switch>
				<Match when={showInitialLoading()}>
					<Shimmer mockData={mockAnswer} count={1}>
						{(data) => (
							<AnswerCard
								item={data}
								onToggleFavourite={() => {}}
								isMetadataPending={() => false}
							/>
						)}
					</Shimmer>
				</Match>
				<Match when={props.answers.error && !props.answers.latest}>
					<Alert variant="error">
						Error loading assets:{" "}
						{props.answers.error?.message ?? "Unknown error"}
					</Alert>
				</Match>
				<Match when={items.length === 0 && !showInitialLoading()}>
					<NoMatchingResults
						message="No artifacts found"
						hint={
							props.searchProps.searchInput().length > 0
								? "Try adjusting your search or filters"
								: "Continue asking questions to generate artifacts"
						}
					/>
				</Match>
				<Match when={items.length > 0}>
					<CardList>
						{/* Outer TransitionGroup: a whole day-bucket appears/disappears
						 * (e.g. the first answer of a new day, or the last answer of an
						 * old one going away) just like an individual card does below -
						 * same collapse technique, one level up. */}
						<TransitionGroup name="answer-group">
							<For each={Object.keys(groupedAnswers())}>
								{(key) => (
									<div class="answer-group-collapse">
										<div class="answer-group-collapse-inner">
											<h3 class="answer-card-group-title">{key}</h3>
											<TransitionGroup name="answer-card">
												<For each={groupedAnswers()[key]}>
													{(item) => (
														<div class="answer-card-collapse">
															<div class="answer-card-collapse-inner">
																<AnswerCard
																	onToggleFavourite={() =>
																		props.toggleFavourite(item.public_id)
																	}
																	item={item}
																	isMetadataPending={props.isMetadataPending}
																	onSelected={props.onAnswerSelected}
																/>
															</div>
														</div>
													)}
												</For>
											</TransitionGroup>
										</div>
									</div>
								)}
							</For>
						</TransitionGroup>
					</CardList>
				</Match>
			</Switch>
		</Stack>
	);
};

const AnswerCard = (props: {
	item: ArtifactListResponseItem;
	onToggleFavourite: () => void;
	isMetadataPending: (public_id: string | undefined) => boolean;
	onSelected?: () => void;
}) => {
	const primaryDocument = () => props.item.documents?.[0] ?? null;
	const fileType = () =>
		primaryDocument()?.file?.content_type?.split("/")?.[1] ?? null;
	const fileSize = () =>
		formatBytesWithMagnitude(primaryDocument()?.file?.bytes ?? 0);
	const docAnchorId = () =>
		props.item.anchor_id ?? `doc-${props.item.public_id ?? ""}`;
	const scrollToDocInChat = (e: MouseEvent) => {
		e.preventDefault();
		const id = docAnchorId();
		if (!id) {
			return;
		}
		const scrollOpts = { behavior: "smooth", block: "start" } as const;
		let el = globalThis.document.getElementById(id);
		// Text answers are anchored on `.response`, not an inline artifact mention.
		if (!el && props.item.kind === "answer" && props.item.artifact_id) {
			const saveControl = globalThis.document.querySelector(
				`.asset-state-button[data-artifact-id="${CSS.escape(props.item.artifact_id)}"]`,
			);
			el =
				(saveControl
					?.closest(".interaction")
					?.querySelector(".answer > .response") as HTMLElement | null) ?? null;
		}
		if (el) {
			el.scrollIntoView(scrollOpts);
			props.onSelected?.();
		}
		const hash = `#${id}`;
		const path = `${window.location.pathname}${window.location.search}${hash}`;
		if (window.location.hash !== hash) {
			history.pushState(null, "", path);
		}
	};

	return (
		<Card class="answer-card">
			<button
				type="button"
				class="gvtui-native-button"
				onClick={scrollToDocInChat}
			>
				<ArtifactItemThumbnail item={props.item} />
			</button>
			<div class="answer-card__grid">
				<button
					type="button"
					class="gvtui-native-button"
					onClick={scrollToDocInChat}
				>
					<Switch>
						<Match when={props.item.title}>
							<div class="answer-card-text">
								<h4>{props.item.title}</h4>
								<span class="answer-card-summary">{props.item.summary}</span>
							</div>
						</Match>
						{/* Title/summary are generated server-side after a save and can
						 * take a few seconds to appear - show a small activity
						 * indicator for however long `isMetadataPending` (useArtifactQuery)
						 * is still waiting on them, rather than jumping straight to
						 * "untitled". */}
						<Match when={props.isMetadataPending(props.item.public_id)}>
							<div class="answer-card-text">
								<h4>-</h4>
								<Text as="span" class="answer-card-summary" color="fg3">
									Generating summary...
								</Text>
							</div>
						</Match>
						<Match when={true}>
							<h4 class="untitled-answer">-</h4>
						</Match>
					</Switch>
				</button>
				<AnswerFavourite
					item={props.item}
					onToggleFavourite={props.onToggleFavourite}
				/>
				<div class="file-details">
					{fileType()} - {fileSize()}
				</div>
				<div class="file-time answer-card__grid-end">
					{timeAgo(props.item.created_at ?? "", "short")}
				</div>
			</div>
		</Card>
	);
};

const AnswerFavourite = (props: {
	item: ArtifactListResponseItem;
	onToggleFavourite: () => void;
}) => {
	const theme: () => { symbol: string; label: string; theme: ButtonTheme } =
		() =>
			props.item.is_favourite
				? { symbol: "check_circle", label: "saved", theme: "success" }
				: { symbol: "bookmark_border", label: "save", theme: "monochrome" };

	return (
		<Button
			class="asset-state-button answer-card__grid-end gvtui-card__actions"
			monospaced
			variant="text"
			noPadding="end"
			theme={theme().theme}
			symbol={theme().symbol}
			symbolFilled={props.item.is_favourite}
			symbolRelativeSize="even-larger"
			symbolPosition="after"
			onClick={() => void props.onToggleFavourite()}
		>
			{theme().label}
		</Button>
	);
};
